import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useLocation } from "react-router";

import type { EventDto } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useEventQueries } from "@/queries/events/queryOptions";

import { type CreateOrEditEventFormValues, EventForm, type EventFormProps, type EventObserver } from "./EventForm";
import { getDefaultFormValues, useAddEvents } from "./Manager";

export function CreateEventPage(): React.ReactNode {
  const sessionUser = useSessionUser();
  const { state } = useLocation();
  const { id: eventIdToDuplicate } = useMemo(
    () => (state && typeof state === "object" && "id" in state ? (state as { id: EventDto["id"] }) : { id: undefined }),
    [state],
  );

  const { create: createEvents, isSubmitting: isSubmittingCreate } = useAddEvents();
  const eventQueries = useEventQueries();
  const {
    data: eventCategories = [],
    isFetching: eventCategoriesIsFetching,
    error: eventCategoriesError,
  } = useQuery(eventQueries.listCategories());

  const {
    data: eventDetails,
    isFetching: eventDetailsIsFetching,
    error: eventDetailsError,
  } = useQuery({
    ...eventQueries.details(
      {
        eventId: eventIdToDuplicate!,
        query: { replaceParameters: false },
      },
      {
        translationMode: "original",
      },
    ),
    enabled: Boolean(eventIdToDuplicate),
  });

  function onSubmit(formValues: CreateOrEditEventFormValues) {
    createEvents(formValues);
  }

  const showAnnouncementModal = eventIdToDuplicate == null;
  const error = eventDetailsError || eventCategoriesError;
  const isDataLoading = eventCategoriesIsFetching || eventDetailsIsFetching;

  if (error) {
    return <ErrorPage error={error} />;
  }

  // A list of projects or project connections that join this asset
  const eventObservers: EventObserver[] = [
    { type: "project", id: sessionUser.project.id, name: sessionUser.project.name },
    ...sessionUser.connections.map((x) => ({
      type: "projectConnection" as const,
      id: x.id,
      name: x.name,
    })),
  ];

  if (isDataLoading) {
    return <FullSizeLoader withPadding />;
  }

  const formProps: EventFormProps = {
    contentLanguageId: eventDetails?.originalLanguageId,
    isEditMode: false,
    canEditAudience: true,
    isResident: !sessionUser.isAdmin,
    defaultFormValues: getDefaultFormValues(
      eventIdToDuplicate != undefined,
      eventCategories,
      eventDetails,
      eventObservers,
    ),
    eventObservers,
    isSubmitting: isSubmittingCreate,
    onSubmit,
    showAnnouncementModal,
    eventCategories: sessionUser.isAdmin ? eventCategories : eventCategories.filter((x) => x.id !== "maintenance"),
    organizer: eventDetails?.organizer || sessionUser,
    isPublished: eventDetails?.publishedAt != null,
  };

  return <EventForm {...formProps} />;
}
